import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, SxProps, Typography } from '@mui/material';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import { usePageIntro } from 'components/PageIntro/PageIntroContext';
import UIContainer from 'components/ui/UIContainer';
import WithLoader from 'components/WithLoader';
import ModeSwitcher from './ModeSwitcher';
import PatternsMode from './PatternsMode';
import FormsMode from './FormsMode';
import FamiliesMode from './FamiliesMode';
import LevelsMode from './LevelsMode';
import { parseBrowseUrl, buildBrowseSearch, ViewMode } from './urlState';
import { FormType, dieslova } from './patternSuperGroups';
import { aggregateByBase, aggregateByPattern, totals } from './aggregations';
import { useAllVerbs } from './useAllVerbs';

const SR_ONLY_SX: SxProps = {
  border: 0,
  clip: 'rect(0, 0, 0, 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

const Stat: React.FC<{
  value: string;
  label: string;
  showDivider?: boolean;
}> = ({ value, label, showDivider }) => (
  <>
    {showDivider && (
      <Box
        sx={{
          alignSelf: { xs: 'stretch', sm: 'center' },
          bgcolor: 'divider',
          height: { xs: 'auto', sm: 32 },
          my: { xs: 0.25, sm: 0 },
          width: '1px',
        }}
      />
    )}
    <Box sx={{ flex: { xs: 1, sm: 'none' }, textAlign: 'center' }}>
      <Typography
        sx={{
          color: 'primary.main',
          fontSize: { xs: 20, md: 24 },
          fontWeight: 800,
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{ color: 'text.secondary', fontSize: { xs: 12, md: 13 }, mt: 0.25 }}
      >
        {label}
      </Typography>
    </Box>
  </>
);

const IrregularVerbBrowsePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = useMemo(() => parseBrowseUrl(searchParams), [searchParams]);

  const { loading, verbs } = useAllVerbs();

  // Wire into the shared page-intro collapse so the breadcrumb toggle hides
  // the hero block on this page too — same behaviour as <PageIntro>, just
  // with a bespoke layout that fits H1 + description + stats into a single
  // surfaced card.
  const { collapsed, registerIntro } = usePageIntro();
  useEffect(() => registerIntro(), [registerIntro]);

  const introTitle = 'Неправильні дієслова';
  const introDescription =
    'Обери, як переглядати — за патернами зміни форм, за типом форми, за сімействами чи за рівнями.';

  const stats = useMemo(() => {
    const t = totals(verbs);
    const learnedPct = t.total > 0 ? Math.round((t.learned / t.total) * 100) : 0;
    return {
      total: t.total,
      learned: t.learned,
      learnedPct,
      patternsCount: aggregateByPattern(verbs).size,
      familiesCount: aggregateByBase(verbs).size,
    };
  }, [verbs]);

  const setView = (view: ViewMode) => {
    const search = buildBrowseSearch({ view, selection: null });
    navigate({ search: search ? `?${search}` : '' });
  };

  // Every drill now navigates to a DEDICATED page. Mirrors how word-sets
  // open `/wordset/level-based/A1/<subcategory>` — clicking a card on the
  // browse grid takes the user to a separate URL instead of inline-drilling
  // on the same page.
  //   pattern → /wordset/irregular-verb/pattern/<patternKey>
  //   form    → /wordset/irregular-verb/form/<AAA|ABB|ABA|ABC>
  //   family  → /wordset/irregular-verb/family/<baseInfinitive>
  //   level   → /wordset/irregular-verb/<level>  (existing CategoryOverview)
  // Legacy `?view=…&pattern|form|base|level=…` URLs still render the inline
  // drill so old bookmarks don't 404.
  const setPattern = (pattern: string) => {
    navigate(`/wordset/irregular-verb/pattern/${encodeURIComponent(pattern)}`);
  };

  const setForm = (form: FormType) => {
    navigate(`/wordset/irregular-verb/form/${encodeURIComponent(form)}`);
  };

  const setBase = (baseVerb: string) => {
    navigate(`/wordset/irregular-verb/family/${encodeURIComponent(baseVerb)}`);
  };

  const setLevel = (level: string) => {
    navigate(`/wordset/irregular-verb/level/${encodeURIComponent(level)}`);
  };

  const clearSelection = () => setView(state.view);

  return (
    <>
      <UIContainer>
        <BreadCrumbs />
      </UIContainer>
      <UIContainer sx={{ pb: { xs: 4, md: 8 } }}>
        {collapsed ? (
          <Typography component={'h1'} sx={SR_ONLY_SX}>
            {introTitle}
          </Typography>
        ) : (
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mb: 2.25,
              mt: 1.5,
              p: { xs: 2, md: '20px 24px' },
            }}
          >
            <Typography
              component={'h1'}
              sx={{
                fontSize: { xs: 24, md: 30, lg: 34 },
                fontWeight: 800,
                lineHeight: 1.15,
                m: 0,
                mb: { xs: 1.75, md: 2.25 },
              }}
            >
              {introTitle}
            </Typography>
            <Box
              sx={{
                alignItems: { xs: 'stretch', md: 'center' },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 4 },
              }}
            >
              <Typography
                sx={{
                  color: 'text.secondary',
                  flex: 1,
                  fontSize: { xs: 14, md: 17 },
                  lineHeight: 1.5,
                  m: 0,
                  maxWidth: 640,
                }}
              >
                {introDescription}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flex: 'none',
                  gap: { xs: 0, sm: 2.5 },
                }}
              >
                <Stat
                  value={String(stats.total)}
                  label={`${dieslova(stats.total)} усього`}
                />
                <Stat
                  value={String(stats.learned)}
                  label={`вивчено · ${stats.learnedPct}%`}
                  showDivider
                />
                <Stat
                  value={`${stats.patternsCount} / ${stats.familiesCount}`}
                  label={'патернів / сімейств'}
                  showDivider
                />
              </Box>
            </Box>
          </Box>
        )}

        <WithLoader loading={loading}>
          <>
            <ModeSwitcher value={state.view} onChange={setView} />
            {state.view === 'patterns' && (
              <PatternsMode
                verbs={verbs}
                selection={state.selection}
                onSelectPattern={setPattern}
                onClearSelection={clearSelection}
              />
            )}
            {state.view === 'forms' && (
              <FormsMode
                verbs={verbs}
                selection={state.selection}
                onSelectForm={setForm}
                onClearSelection={clearSelection}
              />
            )}
            {state.view === 'families' && (
              <FamiliesMode
                verbs={verbs}
                selection={state.selection}
                onSelectBase={setBase}
                onClearSelection={clearSelection}
              />
            )}
            {state.view === 'levels' && (
              <LevelsMode
                verbs={verbs}
                selection={state.selection}
                onSelectLevel={setLevel}
                onClearSelection={clearSelection}
              />
            )}
          </>
        </WithLoader>
      </UIContainer>
    </>
  );
};

export default IrregularVerbBrowsePage;
